import { useEffect, useRef } from "react";

/*
 * The phone's back button, for what the console opens over a page on a phone:
 * a chat over the chat list, the customer's details over the chat, the 更多
 * sheet over the bar. Each one is a step of the browser's history, so back
 * closes the one on top, as it does in WhatsApp. There used to be no step at
 * all, so back inside a chat left the console altogether.
 *
 * A layer closed from the page itself (the arrow, the ✕) takes its step back
 * off with history.back(), which lands a moment later. A URL written in that
 * moment would be written onto the step being left and undone by the landing,
 * so App writes its #page through afterBack().
 */

interface Layer {
  id: number;
  onBack: () => void;
}
const layers: Layer[] = [];
let seq = 0;
/** Backs this module asked for that have not landed yet. */
let pending = 0;
let waiting: Array<() => void> = [];

window.addEventListener("popstate", () => {
  if (pending > 0) {
    pending--;
    if (pending === 0) {
      const run = waiting;
      waiting = [];
      run.forEach((f) => f());
    }
    return;
  }
  layers.pop()?.onBack();
});

/** Opens a layer; back calls onBack. The returned function closes it from the page. */
export function openLayer(onBack: () => void): () => void {
  const id = ++seq;
  layers.push({ id, onBack });
  history.pushState({ ...(history.state ?? {}), lzLayer: id }, "");
  return () => {
    const i = layers.findIndex((l) => l.id === id);
    if (i < 0) return; // back already took it
    layers.splice(i, 1);
    pending++;
    history.back();
  };
}

/** Runs fn once no back of ours is still on its way. */
export function afterBack(fn: () => void): void {
  if (pending === 0) fn();
  else waiting.push(fn);
}

/** The phone layout: one pane at a time, a bar along the bottom. Keep in step with the 800px in the stylesheets. */
export const isPhone = () => window.matchMedia("(max-width: 800px)").matches;

/**
 * A layer for as long as `open` holds, on a phone only: on a wide screen the
 * chat and the list are both on screen and back has nothing to close.
 */
export function useBackLayer(open: boolean, onBack: () => void): void {
  const cb = useRef(onBack);
  cb.current = onBack;
  useEffect(() => {
    if (!open || !isPhone()) return;
    return openLayer(() => cb.current());
  }, [open]);
}
